import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ParamMap } from '@angular/router';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import { Constants, TireFilterCategoryName, WheelFilterCategoryName } from '../core/Constants';
import { API_ENDPOINT } from '../core/DI';
import { CategoryFiltersRequestDto, CategoryFiltersResponseDto, GenericStringValueIdCategoryFilter, SingleGenericStringValueIdCategoryFilter, StringValueIdCategoryFilter } from '../core/models/filter-backend';
import { GenericStaggerCheckboxCategoryFilter } from '../core/models/secondary-filters/generic-stagger-checkbox.category-filter';
import { GenericCategoryFilter } from '../core/models/secondary-filters/generic.category-filter';
import { WheelShowCategoryFilter } from '../core/models/secondary-filters/wheel-show.category-filter';
import { CategoryFilterType, ICategoryFilter } from '../shared/filters/category-filter.model';
import { StringValueId } from '../shared/filters/filter-supported-type.model';
import { GenericCheckboxFilter } from '../shared/filters/generic-checkbox-category-filter/generic-checkbox-category-filter.model';
import { GenericRadioCategoryFilter, GenericRadioFilter } from '../shared/filters/generic-radio-category-filter/generic-radio-category-filter.model';
import { Helper } from './helper';

@Injectable()
export class FilterService {

  private popStateEventSubscription: Subscription;
  private filtersChangeSubject = new Subject<CategoryFiltersRequestDto>();
  private filtersResponseSubject = new Subject<CategoryFiltersResponseDto<StringValueId[]>[]>();
  private filtersResetSubject = new Subject<boolean>();
  private onFilterClearAllSubject = new Subject<boolean>();
  private searchWithFilterInProgress = new Subject<boolean>();
  private backupLastSearchFilter: ICategoryFilter[] = null;

  apiEndpoint: string;

  constructor(
    @Inject(API_ENDPOINT) apiEndpoint: string,
    private http: HttpClient,
    private location: Location) {
    this.apiEndpoint = apiEndpoint;
    this.setPopStateEventListener();
  }

  get onFiltersChangeObservable(): Observable<CategoryFiltersRequestDto> {
    return this.filtersChangeSubject.asObservable();
  }

  get onFilterClearAllObservable(): Observable<boolean> {
    return this.onFilterClearAllSubject.asObservable();
  }

  clearAllFilters(): void {
    return this.onFilterClearAllSubject.next(true);
  }

  onFiltersChange(filters: CategoryFiltersRequestDto): void {
    return this.filtersChangeSubject.next(filters);
  }

  get onFiltersResponseObsevable(): Observable<CategoryFiltersResponseDto<StringValueId[]>[]> {
    return this.filtersResponseSubject.asObservable();
  }

  onFiltersResponse(filters: CategoryFiltersResponseDto<StringValueId[]>[]): void {
    return this.filtersResponseSubject.next(filters);
  }

  get onFiltersResetObservable(): Observable<boolean> {
    return this.filtersResetSubject.asObservable();
  }

  resetFilters(): void {
    this.backupLastSearchFilter = null;
    this.filtersResetSubject.next(true);

    const temp = this.filtersResetSubject;
    this.filtersResetSubject = new Subject<boolean>();
    temp.complete();
  }

  get getBackupLastSearchFilter(): ICategoryFilter[] {
    return this.backupLastSearchFilter;
  }

  set setBackupLastSearchFilter(searchFilter: ICategoryFilter[]) {
    this.backupLastSearchFilter = searchFilter;
  }

  convertCategoryFiltersDtoFromURL(queryParams: ParamMap): CategoryFiltersRequestDto {
    const categoryFiltersRequestDto = new CategoryFiltersRequestDto();
    if (queryParams) {
      queryParams.keys.forEach(category => {


        switch (category) {
          case Constants.MIN_OFFSET_ROUTE_PARAM:
            categoryFiltersRequestDto.minOffset = Number(queryParams.get(category));
            break;
          case Constants.MAX_OFFSET_ROUTE_PARAM:
            categoryFiltersRequestDto.maxOffset = Number(queryParams.get(category));
            break;
          case Constants.PAGE_PARAM:
            categoryFiltersRequestDto.paging.page = Number(queryParams.get(category));
            break;
          case Constants.PAGE_SIZE_PARAM:
            categoryFiltersRequestDto.paging.pageSize = Number(queryParams.get(category));
            break;
          case Constants.ORDER_BY_PARAM:
            categoryFiltersRequestDto.paging.orderBy = queryParams.get(category);
            break;
          case WheelFilterCategoryName.STAGGER:
          case TireFilterCategoryName.STAGGER:
            const categoryFilterDto = new SingleGenericStringValueIdCategoryFilter();
            categoryFilterDto.value = new StringValueId();
            categoryFilterDto.value.id = queryParams.get(category);
            categoryFiltersRequestDto[category] = categoryFilterDto;
            break;
          case Constants.ARCHIVE_PARAM:
            categoryFiltersRequestDto.archive = queryParams.get(category) === "1";
            break;
          default:
            const restoreValues = queryParams.get(category).split(',');
            categoryFiltersRequestDto[category] = new GenericStringValueIdCategoryFilter();
            categoryFiltersRequestDto[category].values = [];
            restoreValues.forEach(value => {
              const item = new StringValueId();
              item.id = value;
              categoryFiltersRequestDto[category].values.push(item);
            });
            break;
        }
      });
    }
    return categoryFiltersRequestDto;
  }

  convertCategoryFiltersFromDto(categoryFiltersDto: CategoryFiltersResponseDto<StringValueId[]>[]): ICategoryFilter[] {
    const categoryFilters = [];
    for (const categoryFilterDto of categoryFiltersDto) {
      let categoryFilter;
      switch (categoryFilterDto.name) {
        case WheelFilterCategoryName.SHOW:
          categoryFilter = this.categoryFiltersResponseDtoToShowCategoryFilter(categoryFilterDto);
          break;
        case WheelFilterCategoryName.STAGGER:
        case TireFilterCategoryName.STAGGER:
          categoryFilter = this.categoryFiltersResponseDtoToGenericRadioCategoryFilter(categoryFilterDto);
          break;
        default:
          categoryFilter = this.categoryFiltersResponseDtoToGenericCategoryFilter(categoryFilterDto);
      }
      categoryFilter.name = categoryFilterDto.name;

      // if it's stagger
      if (Constants.STAGGER_REGEXP.test(categoryFilterDto.name)) {
        categoryFilters.push(new GenericStaggerCheckboxCategoryFilter(categoryFilter));
      } else {
        categoryFilters.push(categoryFilter);
      }
    }

    return categoryFilters;
  }

  convertCategoryFiltersToDto(categoryFilters: ICategoryFilter[]): CategoryFiltersRequestDto {
    // Convert ICategoryFilter<IFilter>[] to CategoryFilterRequestDto
    if (!categoryFilters) {
      return null;
    }

    const categoryFiltersRequestDto = new CategoryFiltersRequestDto();
    for (const categoryFilter of categoryFilters) {
      switch (categoryFilter.type) {
        case CategoryFilterType.Checkbox:
          categoryFiltersRequestDto[categoryFilter.name] =
            this.genericCategoryFilterToCategoryFiltersRequestDto(<GenericCategoryFilter>categoryFilter);
          break;
        case CategoryFilterType.Radio:
          categoryFiltersRequestDto[categoryFilter.name] =
            this.genericRadioCategoryFilterToCategoryFiltersRequestDto(<GenericRadioCategoryFilter>categoryFilter);
          break;
      }
    }

    return categoryFiltersRequestDto;
  }

  // TODO: could it's be replace by automapper
  // Mapper
  private categoryFiltersResponseDtoToGenericCategoryFilter(categoryFilterDto: CategoryFiltersResponseDto<StringValueId[]>)
    : GenericCategoryFilter {
    const categoryFilter = new GenericCategoryFilter();
    categoryFilter.isAllChecked = true;

    categoryFilter.filters = categoryFilterDto.items.map(value => {
      const stringValueIdCheckboxFilter = new GenericCheckboxFilter();
      stringValueIdCheckboxFilter.isChecked = false;
      stringValueIdCheckboxFilter.value = value;
      return stringValueIdCheckboxFilter;
    });

    return categoryFilter;
  }

  private categoryFiltersResponseDtoToShowCategoryFilter(categoryFilterDto: CategoryFiltersResponseDto<StringValueId[]>)
    : WheelShowCategoryFilter {

    const categoryFilter = new WheelShowCategoryFilter();
    categoryFilter.isAllChecked = false;
    categoryFilter.filters = categoryFilterDto.items.map(item => {
      const stringValueIdCheckboxFilter = new GenericCheckboxFilter();
      //stringValueIdCheckboxFilter.isChecked = item.setValue == '1' ? true : false;
      stringValueIdCheckboxFilter.isChecked = item.defaultValue === '1' ? true : false;
      stringValueIdCheckboxFilter.value = item;
      return stringValueIdCheckboxFilter;
    });

    return categoryFilter;
  }

  private categoryFiltersResponseDtoToGenericRadioCategoryFilter(categoryFilterDto: CategoryFiltersResponseDto<StringValueId[]>)
    : GenericRadioCategoryFilter {

    const categoryFilter = new GenericRadioCategoryFilter();
    categoryFilter.filters = categoryFilterDto.items.map(item => {
      const stringValueIdCheckboxFilter = new GenericRadioFilter();
      stringValueIdCheckboxFilter.value = item;
      return stringValueIdCheckboxFilter;
    });

    return categoryFilter;
  }

  private genericRadioCategoryFilterToCategoryFiltersRequestDto(categoryFilter: GenericRadioCategoryFilter)
    : SingleGenericStringValueIdCategoryFilter {
    const categoryFilterDto = new SingleGenericStringValueIdCategoryFilter();
    if (categoryFilter.selected) {
      categoryFilterDto.value = categoryFilter.selected.value;
    } else {
      categoryFilterDto.value = new StringValueId();
      categoryFilterDto.value.id = '';
    }

    return categoryFilterDto;
  }

  private genericCategoryFilterToCategoryFiltersRequestDto(categoryFilter: GenericCategoryFilter): StringValueIdCategoryFilter {
    if (categoryFilter.isAllChecked) {
      return null;
    }

    const categoryFilterDto = new StringValueIdCategoryFilter();
    categoryFilterDto.values = [];

    for (const filter of categoryFilter.filters) {
      if (filter.isChecked) {
        categoryFilterDto.values.push(filter.value);
        categoryFilterDto.values.sort((x, y) => x.value > y.value ? 1 : x.value < y.value ? -1 : 0);
      }
    }

    return categoryFilterDto;
  }

  private setPopStateEventListener() {
    if (this.popStateEventSubscription) {
      this.popStateEventSubscription.unsubscribe();
    }

    this.popStateEventSubscription = this.location.subscribe(() => {
      // On browser back/forward
      this.resetFilters();
    }, Helper.handleError) as Subscription;
  }
}
